// Implements: F-UIX-019, F-RPT-006, F-OBS-003
/**
 * Mockup v3 P9 Estado "Latencia y errores por modelo y paso" (v3 `:1916-1919`, default P4-1): the
 * platform admin picks an organization (the platform's organization list, first one by default)
 * and a period ("Este mes" or "Últimos 7 días"), and reads that organization's latency through
 * `GET /reports/platform/latency`, read-only.
 *
 * Every read is cached by organization and window (`platformLatencyKey`, checklist 14): an answer
 * still on its way for the organization left behind is never drawn for the next one. The day the
 * period ends on is handed in (today by default, frozen on mount: checklist 16). With no
 * organization nothing is asked. Loading, error (with its retry) and empty states are the
 * section's own (F-UIX-010). "Exportar CSV" (owner 2026-10-19) downloads the organization and period
 * on screen through the report's export twin; it is offered only once there is an organization.
 */

import { useQuery } from "@tanstack/react-query";
import { useId, useState } from "react";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { platformLatencyKey } from "../../../shared/api/queryKeys";
import { useTranslation } from "../../../shared/i18n";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { TableSkeleton } from "../../../shared/ui/molecules/TableSkeleton";
import { lastDays, monthToDate } from "../../../shared/ui/reportWindows";
import type { LatencyReportView, ReportWindow } from "../../../types/reports";
import { PLATFORM_LATENCY_EXPORT_NAME, downloadPlatformLatency, fetchPlatformLatency } from "../api/platformLatencyApi";
import styles from "../components/Latency.module.css";
import { LatencyTable } from "../components/LatencyTable";
import { useOrganizationList } from "../hooks/usePlatformTotals";
import { ExportCsvContainer } from "./ExportCsvContainer";
import { dayLabel } from "../model/days";

type Period = "month" | "week";

const WEEK_DAYS = 7;

export interface PlatformLatencyContainerProps {
  /** The day the period ends on; today by default. */
  readonly today?: Date | undefined;
}

function windowOf(period: Period, today: Date): ReportWindow {
  return period === "month" ? monthToDate(today) : lastDays(today, WEEK_DAYS);
}

export function PlatformLatencyContainer({ today }: PlatformLatencyContainerProps): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const organizationId = useId();
  const periodId = useId();
  const [day] = useState<Date>(() => today ?? new Date());
  const [chosen, setChosen] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>("month");
  const organizations = useOrganizationList();
  const listed = organizations.data?.organizations ?? [];
  const organization = listed.find((candidate) => candidate.id === chosen) ?? listed[0];
  const window = windowOf(period, day);
  const latency = useQuery<LatencyReportView, ApiError>({
    queryKey: platformLatencyKey(organization?.id ?? "", window.from, window.to),
    queryFn: ({ signal }) => fetchPlatformLatency(organization?.id ?? "", window, signal),
    enabled: organization !== undefined,
  });

  const body = ((): ReactElement => {
    if (organizations.isError) {
      return (
        <ErrorState
          headingLevel={3}
          error={asApiError(organizations.error)}
          onRetry={() => {
            void organizations.refetch();
          }}
        />
      );
    }
    if (organizations.data === undefined) return <TableSkeleton />;
    if (organization === undefined) {
      return (
        <EmptyState
          icon="chart"
          headingLevel={3}
          title={t("reports.latency.noOrganizations.title")}
          body={t("reports.latency.noOrganizations.body")}
        />
      );
    }
    if (latency.isError) {
      return (
        <ErrorState
          headingLevel={3}
          error={asApiError(latency.error)}
          onRetry={() => {
            void latency.refetch();
          }}
        />
      );
    }
    if (latency.data === undefined) return <TableSkeleton />;
    if (latency.data.rows.length === 0) {
      return <EmptyState icon="chart" headingLevel={3} title={t("reports.latency.empty.title")} body={t("reports.latency.empty.body")} />;
    }
    return <LatencyTable rows={latency.data.rows} />;
  })();

  return (
    <section className={styles.card} aria-labelledby={headingId}>
      <div className={styles.head}>
        <div className={styles.titles}>
          <h2 id={headingId} className={styles.title}>
            {t("reports.latency.title")}
          </h2>
          {organization === undefined ? null : (
            <p className={styles.meta}>
              {t("reports.latency.meta", { organization: organization.name, from: dayLabel(window.from), to: dayLabel(window.to) })}
            </p>
          )}
        </div>
        {listed.length > 0 && organization !== undefined ? (
          <label className={styles.field} htmlFor={organizationId}>
            {t("reports.latency.organization")}
            <select
              id={organizationId}
              className={styles.select}
              value={organization.id}
              onChange={(event) => {
                setChosen(event.target.value);
              }}
            >
              {listed.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className={styles.field} htmlFor={periodId}>
          {t("reports.latency.period")}
          <select
            id={periodId}
            className={styles.select}
            value={period}
            onChange={(event) => {
              setPeriod(event.target.value === "week" ? "week" : "month");
            }}
          >
            <option value="month">{t("reports.latency.period.month")}</option>
            <option value="week">{t("reports.latency.period.week")}</option>
          </select>
        </label>
        {organization === undefined ? null : (
          <ExportCsvContainer
            download={() => downloadPlatformLatency(organization.id, window)}
            fallbackName={PLATFORM_LATENCY_EXPORT_NAME}
          />
        )}
      </div>
      {body}
    </section>
  );
}
